import { FC } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ProtectedMatchmakerRoute } from "@/components/ProtectedMatchmakerRoute";
import { ProtectedAdminRoute } from "@/components/ProtectedAdminRoute";
import Landing from "./pages/Landing";
import Signup from "./pages/Signup";
import Login from "./pages/Login";
import Welcome from "./pages/Welcome";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import ProfileCreation from "./pages/ProfileCreation";
import RequestJoin from "./pages/RequestJoin";
import MatchesList from "./pages/MatchesList";
import MatchDetail from "./pages/MatchDetail";
import MatchmakerProfiles from "./pages/MatchmakerProfiles";
import MatchmakerCreateProfile from "./pages/MatchmakerCreateProfile";
import MatchmakerJoinRequests from "./pages/MatchmakerJoinRequests";
import MatchmakerJoinRequestDetail from "./pages/MatchmakerJoinRequestDetail";
import ClaimProfile from "./pages/ClaimProfile";
import SmartMatcher from "./pages/SmartMatcher";
import MutualMatches from "./pages/MutualMatches";
import MatchHistory from "./pages/MatchHistory";
import MatchmakerAnalytics from "./pages/MatchmakerAnalytics";
import AdminFeedback from "./pages/AdminFeedback";
import MatchmakerSignup from "./pages/MatchmakerSignup";
import AdminMatchmakerSignups from "./pages/AdminMatchmakerSignups";
import AdminMatchmakerSignupDetail from "./pages/AdminMatchmakerSignupDetail";
import Settings from "./pages/Settings";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Terms from "./pages/Terms";
import MatchmakerGuide from "./pages/MatchmakerGuide";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient();

const App: FC = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AuthProvider>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/login" element={<Login />} />
            <Route path="/request-join" element={<RequestJoin />} />
            <Route path="/matchmaker/signup" element={<MatchmakerSignup />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/welcome" element={<Welcome />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/claim-profile/:token" element={<ClaimProfile />} />
            <Route
              path="/profile/create"
              element={
                <ProtectedRoute>
                  <ProfileCreation />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/matches"
              element={
                <ProtectedRoute>
                  <MatchesList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/matches/:matchId"
              element={
                <ProtectedRoute>
                  <MatchDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/matchmaker/profiles"
              element={
                <ProtectedMatchmakerRoute>
                  <MatchmakerProfiles />
                </ProtectedMatchmakerRoute>
              }
            />
            <Route
              path="/matchmaker/create-profile"
              element={
                <ProtectedMatchmakerRoute>
                  <MatchmakerCreateProfile />
                </ProtectedMatchmakerRoute>
              }
            />
            <Route
              path="/matchmaker/smart-matcher"
              element={
                <ProtectedMatchmakerRoute>
                  <SmartMatcher />
                </ProtectedMatchmakerRoute>
              }
            />
            <Route
              path="/matchmaker/mutual-matches"
              element={
                <ProtectedMatchmakerRoute>
                  <MutualMatches />
                </ProtectedMatchmakerRoute>
              }
            />
            <Route
              path="/matchmaker/history"
              element={
                <ProtectedMatchmakerRoute>
                  <MatchHistory />
                </ProtectedMatchmakerRoute>
              }
            />
            <Route
              path="/matchmaker/analytics"
              element={
                <ProtectedMatchmakerRoute>
                  <MatchmakerAnalytics />
                </ProtectedMatchmakerRoute>
              }
            />
            <Route
              path="/matchmaker/join-requests"
              element={
                <ProtectedMatchmakerRoute>
                  <MatchmakerJoinRequests />
                </ProtectedMatchmakerRoute>
              }
            />
            <Route
              path="/matchmaker/join-requests/:profileId"
              element={
                <ProtectedMatchmakerRoute>
                  <MatchmakerJoinRequestDetail />
                </ProtectedMatchmakerRoute>
              }
            />
            <Route
              path="/matchmaker/guide"
              element={
                <ProtectedMatchmakerRoute>
                  <MatchmakerGuide />
                </ProtectedMatchmakerRoute>
              }
            />
            <Route
              path="/dashboard/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/profiles"
              element={
                <ProtectedAdminRoute>
                  <MatchmakerProfiles />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/smart-matcher"
              element={
                <ProtectedAdminRoute>
                  <SmartMatcher />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/mutual-matches"
              element={
                <ProtectedAdminRoute>
                  <MutualMatches />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/history"
              element={
                <ProtectedAdminRoute>
                  <MatchHistory />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/analytics"
              element={
                <ProtectedAdminRoute>
                  <MatchmakerAnalytics />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/feedback"
              element={
                <ProtectedAdminRoute>
                  <AdminFeedback />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/matchmaker-signups"
              element={
                <ProtectedAdminRoute>
                  <AdminMatchmakerSignups />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/matchmaker-signups/:id"
              element={
                <ProtectedAdminRoute>
                  <AdminMatchmakerSignupDetail />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/join-requests"
              element={
                <ProtectedAdminRoute>
                  <MatchmakerJoinRequests />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/join-requests/:profileId"
              element={
                <ProtectedAdminRoute>
                  <MatchmakerJoinRequestDetail />
                </ProtectedAdminRoute>
              }
            />
            <Route
              path="/admin/create-profile"
              element={
                <ProtectedAdminRoute>
                  <MatchmakerCreateProfile />
                </ProtectedAdminRoute>
              }
            />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
